// Filter popover for Bank account history (opens from the funnel icon).
// Find · Reconcile Status · Transaction Type · Payee · Date preset · From · To · Reset · Apply.

import React, { useEffect, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import type { BankHistoryFilter } from "../types";
import {
  RECONCILE_STATUS_OPTIONS,
  TRANSACTION_TYPE_OPTIONS,
  DATE_PRESET_OPTIONS,
  DEFAULT_BANK_HISTORY_FILTER,
} from "../constants";

interface BankHistoryFilterPanelProps {
  open: boolean;
  onClose: () => void;
  initial: BankHistoryFilter;
  onApply: (f: BankHistoryFilter) => void;
  payees: string[];
}

export default function BankHistoryFilterPanel({ open, onClose, initial, onApply, payees }: BankHistoryFilterPanelProps) {
  const [draft, setDraft] = useState<BankHistoryFilter>(initial);

  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  if (!open) return null;

  const update = (patch: Partial<BankHistoryFilter>) => setDraft((d) => ({ ...d, ...patch }));
  const handleReset = () => setDraft(DEFAULT_BANK_HISTORY_FILTER);
  const handleApply = () => { onApply(draft); onClose(); };

  const showCustomRange = draft.datePreset === "custom";

  return (
    <div className="absolute left-0 top-full mt-1.5 bg-white border border-line rounded-xl shadow-[0_10px_32px_rgba(14,26,26,0.14)] p-5 z-50" style={{ width: 720 }}>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
          All
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="text-ink3 border-none bg-transparent cursor-pointer hover:text-ink">
          <X size={16} />
        </button>
      </div>

      <label className="block text-[12px] font-semibold text-ink2 mb-1.5">Find</label>
      <input
        type="text"
        value={draft.find}
        onChange={(e) => update({ find: e.target.value })}
        placeholder="Memo, Ref no, $amt, >$amt, <$amt"
        className="w-full border border-line rounded-md px-3 py-2 text-[12.5px] text-ink placeholder:text-ink4 outline-none focus:border-teal-dark mb-4"
      />

      <div className="grid grid-cols-3 gap-3.5 mb-3.5">
        <div>
          <label className="block text-[12px] font-semibold text-ink2 mb-1.5">Reconcile Status</label>
          <SelectBox value={draft.reconcileStatus} onChange={(v) => update({ reconcileStatus: v as BankHistoryFilter["reconcileStatus"] })}>
            {RECONCILE_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </SelectBox>
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-ink2 mb-1.5">Transaction Type</label>
          <SelectBox value={draft.transactionType} onChange={(v) => update({ transactionType: v as BankHistoryFilter["transactionType"] })}>
            {TRANSACTION_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </SelectBox>
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-ink2 mb-1.5">Payee</label>
          <SelectBox value={draft.payee} onChange={(v) => update({ payee: v })}>
            <option value="all">All</option>
            {payees.map((p) => <option key={p} value={p}>{p}</option>)}
          </SelectBox>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3.5 mb-4">
        <div>
          <label className="block text-[12px] font-semibold text-ink2 mb-1.5">Date</label>
          <SelectBox value={draft.datePreset} onChange={(v) => update({ datePreset: v as BankHistoryFilter["datePreset"] })}>
            {DATE_PRESET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </SelectBox>
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-ink2 mb-1.5">From</label>
          <input type="date" disabled={!showCustomRange} value={draft.fromDate} onChange={(e) => update({ fromDate: e.target.value })} className="w-full border border-line rounded-md px-3 py-2 text-[12.5px] text-ink outline-none focus:border-teal-dark disabled:bg-panel disabled:text-ink4" />
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-ink2 mb-1.5">To</label>
          <input type="date" disabled={!showCustomRange} value={draft.toDate} onChange={(e) => update({ toDate: e.target.value })} className="w-full border border-line rounded-md px-3 py-2 text-[12.5px] text-ink outline-none focus:border-teal-dark disabled:bg-panel disabled:text-ink4" />
        </div>
      </div>

      <div className="flex justify-between items-center pt-3.5 border-t border-lineSoft">
        <button type="button" onClick={handleReset} className="text-ink3 text-[12.5px] font-bold px-4 py-2 border border-line rounded-full bg-transparent cursor-pointer hover:text-ink">
          Reset
        </button>
        <button type="button" onClick={handleApply} className="bg-teal-dark text-white text-[12.5px] font-bold px-6 py-2 rounded-full border-none cursor-pointer hover:brightness-110">
          Apply
        </button>
      </div>
    </div>
  );
}

function SelectBox({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: React.ReactNode }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full appearance-none border border-line rounded-md px-3 pr-9 py-2 text-[12.5px] text-ink bg-white cursor-pointer outline-none focus:border-teal-dark">
        {children}
      </select>
      <ChevronDown size={12} strokeWidth={2.5} className="text-ink3 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}